import { reactive, computed, watch, toRefs, type Ref } from 'vue'

export interface AccountFormState<T> {
  data: T
  originalData: T
  isEditing: boolean
  saving: boolean
  error: string
  success: boolean
}

export interface UseAccountFormStateReturn<T> {
  formData: Ref<T>
  originalData: Ref<T>
  isEditing: Ref<boolean>
  saving: Ref<boolean>
  error: Ref<string>
  success: Ref<boolean>
  isDirty: Ref<boolean>
  activateForm: () => void
  revokeChanges: () => void
  markAsSaving: () => void
  markAsSaved: () => void
  markAsFailed: (errorMessage?: string) => void
  resetStatus: () => void
}

/**
 * Composable for managing account form state with edit/save/undo functionality.
 *
 * @param initialData - Ref containing the initial form data
 * @param options - Optional configuration
 * @returns Form state and control functions
 *
 * @example
 * ```ts
 * const { formData, isEditing, isDirty, activateForm, revokeChanges, markAsSaved } =
 *   useAccountFormState(toRef(props, 'accountDetails'))
 * ```
 */
export function useAccountFormState<T extends Record<string, unknown>>(
  initialData: Ref<T>,
  options?: {
    /** Callback when form is activated */
    onActivate?: () => void
    /** Callback when changes are revoked */
    onRevoke?: () => void
    /** Duration in ms to show success state (default: 3000) */
    successDuration?: number
  }
): UseAccountFormStateReturn<T> {
  const { onActivate, onRevoke, successDuration = 3000 } = options || {}

  const state = reactive({
    data: { ...initialData.value },
    originalData: { ...initialData.value },
    isEditing: false,
    saving: false,
    error: '',
    success: false
  })

  // Compute if form has unsaved changes (only when editing)
  const isDirty = computed(() => {
    if (!state.isEditing) return false
    return JSON.stringify(state.data) !== JSON.stringify(state.originalData)
  })

  // Enter edit mode
  const activateForm = () => {
    state.isEditing = true
    state.error = ''
    state.success = false
    // Store current data as original for potential undo
    state.originalData = { ...state.data }
    onActivate?.()
  }

  // Cancel editing and revert to original data
  const revokeChanges = () => {
    state.isEditing = false
    state.data = { ...state.originalData }
    state.error = ''
    onRevoke?.()
  }

  // Mark form as saving (for loading state)
  const markAsSaving = () => {
    state.saving = true
    state.error = ''
  }

  // Mark form as successfully saved
  const markAsSaved = () => {
    state.saving = false
    state.isEditing = false
    state.error = ''
    state.success = true
    // Update original to match saved data
    state.originalData = { ...state.data }

    // Auto-hide success after duration
    setTimeout(() => {
      state.success = false
    }, successDuration)
  }

  // Mark form as failed with optional error message
  const markAsFailed = (errorMessage?: string) => {
    state.saving = false
    state.error = errorMessage || ''
    // Revert to original on failure
    state.data = { ...state.originalData }
  }

  // Reset status flags without changing data
  const resetStatus = () => {
    state.error = ''
    state.success = false
  }

  // Sync with prop changes when not editing
  watch(
    initialData,
    (newData) => {
      if (!state.isEditing) {
        Object.assign(state.data, newData)
        Object.assign(state.originalData, newData)
      }
    },
    { deep: true }
  )

  const refs = toRefs(state)

  return {
    formData: refs.data as Ref<T>,
    originalData: refs.originalData as Ref<T>,
    isEditing: refs.isEditing,
    saving: refs.saving,
    error: refs.error,
    success: refs.success,
    isDirty,
    activateForm,
    revokeChanges,
    markAsSaving,
    markAsSaved,
    markAsFailed,
    resetStatus
  }
}
